"use client";
import type { ReactElement } from "react";
import { cn } from "@/lib/cn";
import { formatTimeInZone } from "@/lib/timezones";
import type { SlotEntry } from "@/lib/types";

type Props = {
  slots: SlotEntry[];
  selectedSlot: SlotEntry | null;
  onSelectSlot: (slot: SlotEntry) => void;
  timezone: string;
  loading: boolean;
};

function SlotSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-12 w-full animate-pulse rounded-xl bg-neutral-100" />
      ))}
    </div>
  );
}

export function TimeSlotList({ slots, selectedSlot, onSelectSlot, timezone, loading }: Props): ReactElement {
  if (loading) return <SlotSkeleton />;

  if (slots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
          <svg className="h-5 w-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-neutral-500">No available times</p>
        <p className="mt-1 text-xs text-neutral-400">Try a different date</p>
      </div>
    );
  }

  return (
    <div className="max-h-[440px] overflow-y-auto pr-1">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {slots.map((slot, idx) => {
          const isSelected =
            selectedSlot?.startTime === slot.startTime &&
            selectedSlot?.endTime === slot.endTime;
          const timeLabel = formatTimeInZone(slot.startTime, timezone);

          return (
            <button
              key={idx}
              onClick={() => onSelectSlot(slot)}
              className={cn(
                "group relative flex items-center justify-center rounded-xl border py-3.5 text-sm font-semibold transition-all duration-150",
                isSelected
                  ? "border-[#029391] bg-[#029391] text-white shadow-md"
                  : "border-neutral-200 bg-white text-neutral-700 hover:border-[#029391] hover:bg-[#E8F9F9] hover:text-[#029391] hover:shadow-sm"
              )}
            >
              {isSelected && (
                <svg className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {timeLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}
